// ---------------------------------------------------------------------------
// Live sync — Socket.IO client. One connection per tab; joins `area:<id>`
// rooms for whatever project is on screen and applies authoritative
// `task:sync` events into the tasks store (skipping this tab's own writes).
// ---------------------------------------------------------------------------

import { io, type Socket } from 'socket.io-client';
import { apiOrigin, getAuthToken, CLIENT_ID } from '@/core/api/apiClient';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useToastStore } from '@/core/ui/toastStore';
import type { TaskMessage, TaskSyncEvent, UserNotifyEvent, AreaGoneEvent } from '@cg-projects/shared';

export interface TaskMessageEvent {
  areaId: number;
  taskId: number;
  message: TaskMessage;
  originClientId?: string;
  /** Odoo uid of whoever posted it — the echo-guard is per TAB, so my own
   *  comment does reach my other tabs and must still show Edit/Delete. */
  authorUid?: number;
}

let socket: Socket | null = null;
// Refcounted: the selected board AND an open task-detail panel can both hold the
// same area room; the socket leaves it only when the last holder releases.
const joined = new Map<number, number>();
// Task-level rooms, refcounted like `joined` — for a non-member assignee whose
// open panel is their only live channel (join:area would refuse them).
const joinedTasks = new Map<number, number>();
const messageListeners = new Set<(e: TaskMessageEvent) => void>();
const notifyListeners = new Set<(e: UserNotifyEvent) => void>();
const syncListeners = new Set<(e: TaskSyncEvent) => void>();

export type CommentUpdateEvent =
  | { areaId: number; taskId: number; action: 'edit'; message: TaskMessage; originClientId?: string }
  | { areaId: number; taskId: number; action: 'delete'; msgId: number; originClientId?: string };
const commentUpdateListeners = new Set<(e: CommentUpdateEvent) => void>();

/** Subscribe to live comments (returns an unsubscribe fn). */
export function onTaskMessage(cb: (e: TaskMessageEvent) => void): () => void {
  messageListeners.add(cb);
  return () => messageListeners.delete(cb);
}

/** Subscribe to comment edit/delete events. */
export function onCommentUpdate(cb: (e: CommentUpdateEvent) => void): () => void {
  commentUpdateListeners.add(cb);
  return () => commentUpdateListeners.delete(cb);
}

/** Subscribe to personal pings — assigned / mentioned / verdict. */
export function onUserNotify(cb: (e: UserNotifyEvent) => void): () => void {
  notifyListeners.add(cb);
  return () => notifyListeners.delete(cb);
}

/** Subscribe to raw task syncs (the open task panel refreshes itself live). */
export function onTaskSync(cb: (e: TaskSyncEvent) => void): () => void {
  syncListeners.add(cb);
  return () => syncListeners.delete(cb);
}

function ensureSocket(): Socket | null {
  const token = getAuthToken();
  if (!token) return null;
  if (socket) return socket;

  socket = io(apiOrigin(), {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnectionDelayMax: 15_000,
  });

  let hadConnection = false;
  socket.on('connect', () => {
    // Re-join rooms after any reconnect.
    for (const id of joined.keys()) socket!.emit('join:area', id);
    for (const id of joinedTasks.keys()) socket!.emit('join:task', id);
    // Reconnect = we may have missed events — refetch joined areas so boards
    // never sit silently stale.
    if (hadConnection) {
      const st = useTasksStore.getState();
      for (const id of joined.keys()) void st.loadTasks(id, st.loadedClosed[id] ?? false, true);
    }
    hadConnection = true;
  });

  // The server ended this session (an admin signed us out, or our Odoo user was
  // archived). It sends this BEFORE dropping the socket, because otherwise
  // Socket.IO just reconnect-loops forever against a handshake that now refuses
  // us — a spinning tab with no explanation. Handing it to the same 401 path the
  // REST client uses means one behaviour: sign out, say why.
  socket.on('session:ended', (e: { reason?: string }) => {
    console.warn('[socket] session ended by server:', e?.reason ?? 'revoked');
    stopReconnecting();
    onSessionEnded?.();
  });

  socket.on('task:sync', (e: TaskSyncEvent) => {
    if (!e || !e.task) return;
    if (e.originClientId && e.originClientId === CLIENT_ID) return; // our own write
    useTasksStore.getState().applyRemote(e);
    for (const cb of syncListeners) cb(e);
  });

  socket.on('user:notify', (e: UserNotifyEvent) => {
    if (!e) return;
    for (const cb of notifyListeners) cb(e);
  });

  // Bulk server-side change (e.g. offboard reassigned 20 tasks, manual reorder)
  // → refetch the whole area instead of waiting for many syncs. Skip our own
  // echo so the acting tab doesn't refetch (and flicker) on its own reorder.
  socket.on('area:refresh', (e: { areaId: number; originClientId?: string }) => {
    if (!e || !Number.isInteger(e.areaId)) return;
    if (e.originClientId && e.originClientId === CLIENT_ID) return;
    const st = useTasksStore.getState();
    if (st.tasksByArea[e.areaId]) void st.loadTasks(e.areaId, st.loadedClosed[e.areaId] ?? false, true);
  });

  socket.on('task:message', (e: TaskMessageEvent) => {
    if (!e || !e.message) return;
    if (e.originClientId && e.originClientId === CLIENT_ID) return; // our own comment
    for (const cb of messageListeners) cb(e);
  });

  socket.on('comment:update', (e: CommentUpdateEvent) => {
    if (!e) return;
    if (e.originClientId && e.originClientId === CLIENT_ID) return; // our own edit/delete
    for (const cb of commentUpdateListeners) cb(e);
  });

  // The project was archived out from under us. Without this the board just
  // stops updating and the project lingers in the sidebar until a reload.
  socket.on('area:gone', (e: AreaGoneEvent) => {
    if (!e?.areaId) return;
    const area = useAreaStore.getState();
    void area.loadAreas(true); // drops it from the sidebar
    // Honest message per reason — it was NOT always "archived" (un-publish went
    // members-only; a removal is a removal).
    const msg = e.reason === 'private'
      ? 'That project is now members-only — ask its lead to add you.'
      : e.reason === 'removed'
        ? 'You were removed from that project.'
        : 'That project was archived — showing your home view.';
    if (area.selectedAreaId === e.areaId) {
      // FromRoute = replace, not push: losing access is not a navigation, and a
      // pushed entry would let Back walk right back into the project.
      area.goHomeFromRoute();
      useToastStore.getState().show(msg, 'info');
    }
    useTasksStore.setState((s) => {
      const tba = { ...s.tasksByArea };
      delete tba[e.areaId];
      return { tasksByArea: tba };
    });
    joined.delete(e.areaId);
    // The server just dropped us from this area's TASK rooms too (leak fix). Re-verify
    // any open task: an ASSIGNEE keeps task-level access and is re-added live; anyone
    // who fully lost access is refused, so no more of that task's events reach them.
    for (const id of joinedTasks.keys()) socket!.emit('join:task', id);
  });

  return socket;
}

/** Connect early — personal pings must work even sitting on Home. */
export function connectSocket(): void {
  ensureSocket();
}

/** Join a project's live-update room (refcounted). */
export function joinArea(areaId: number): void {
  const s = ensureSocket();
  if (!s) return;
  const c = joined.get(areaId) ?? 0;
  joined.set(areaId, c + 1);
  if (c === 0) s.emit('join:area', areaId); // actually join only on 0→1
}

export function leaveArea(areaId: number): void {
  if (!socket) return;
  const c = joined.get(areaId) ?? 0;
  if (c <= 1) {
    joined.delete(areaId);
    socket.emit('leave:area', areaId); // actually leave only when the last holder releases
  } else {
    joined.set(areaId, c - 1);
  }
}

/** Join a single task's live-update room (refcounted) — the live channel for a
 *  non-member assignee, whose open panel is their only view of the task. Members
 *  already get it via the area room; the server dedupes the overlap. */
export function joinTask(taskId: number): void {
  const s = ensureSocket();
  if (!s) return;
  const c = joinedTasks.get(taskId) ?? 0;
  joinedTasks.set(taskId, c + 1);
  if (c === 0) s.emit('join:task', taskId);
}

export function leaveTask(taskId: number): void {
  if (!socket) return;
  const c = joinedTasks.get(taskId) ?? 0;
  if (c <= 1) {
    joinedTasks.delete(taskId);
    socket.emit('leave:task', taskId);
  } else {
    joinedTasks.set(taskId, c - 1);
  }
}

/** Tear down on logout so a new login re-authenticates the socket. */
export function disconnectSocket(): void {
  joined.clear();
  joinedTasks.clear();
  socket?.disconnect();
  socket = null;
}

/** Tear the connection down WITHOUT letting socket.io retry. Used when the server
 *  says the session is over: reconnecting can only fail, and each attempt costs a
 *  handshake + an Odoo liveness read. */
function stopReconnecting(): void {
  joined.clear();
  joinedTasks.clear();
  if (socket) {
    socket.io.opts.reconnection = false;
    socket.disconnect();
    socket = null;
  }
}

// Registered by the auth store (same handler as a 401), so "your session ended"
// looks identical whether REST or the socket noticed first.
let onSessionEnded: (() => void) | null = null;
export function setSessionEndedHandler(fn: () => void): void {
  onSessionEnded = fn;
}
